import { initializeApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth'
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  arrayUnion,
  serverTimestamp,
} from 'firebase/firestore'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)

const googleProvider = new GoogleAuthProvider()

// ============================================================
// Auth
// ============================================================

export const loginWithGoogle = async () => {
  const result = await signInWithPopup(auth, googleProvider)
  await ensureUserDocChemcraft(result.user)
  return result.user
}

export const loginWithEmail = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password)
  await ensureUserDocChemcraft(result.user)
  return result.user
}

export const registerWithEmail = async (email, password) => {
  const result = await createUserWithEmailAndPassword(auth, email, password)
  await ensureUserDocChemcraft(result.user)
  return result.user
}

export const logout = () => signOut(auth)

export const onAuthChange = (callback) => onAuthStateChanged(auth, callback)

export const getUserData = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid))
  return snap.exists() ? snap.data() : null
}

/**
 * ChemCraft 用のユーザードキュメントを作成または更新。
 * craft シリーズ共通の users/{uid} を使う。
 * - 新規: planChem:'free', sources:['chemcraft'] で作成
 * - 既存: sources に 'chemcraft' を追加（他アプリのユーザーが来た場合）
 */
const ensureUserDocChemcraft = async (user) => {
  const ref = doc(db, 'users', user.uid)
  const snap = await getDoc(ref)
  if (!snap.exists()) {
    await setDoc(ref, {
      email: user.email,
      displayName: user.displayName || '',
      planChem: 'free',
      scoresChem: {},           // { questionId: bestScore }
      completedChem: [],        // 完了済み問題IDリスト
      sources: ['chemcraft'],
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    })
  } else {
    await updateDoc(ref, {
      sources: arrayUnion('chemcraft'),
      lastLoginAt: serverTimestamp(),
    })
  }
}

// ============================================================
// Firestore
// ============================================================

/** スコア保存（ベストスコアのみ更新） */
export const saveScore = async (uid, questionId, score, isCorrect) => {
  const ref = doc(db, 'users', uid)
  const snap = await getDoc(ref)
  if (!snap.exists()) return
  const data = snap.data()
  const currentScores = data.scoresChem || {}
  const bestScore = Math.max(currentScores[questionId] || 0, score)
  const update = { [`scoresChem.${questionId}`]: bestScore }
  if (isCorrect) {
    update.completedChem = arrayUnion(questionId)
  }
  await updateDoc(ref, update)
}

/** 問い合わせ送信 */
export const submitInquiry = async ({ name, email, message, user }) => {
  await addDoc(collection(db, 'inquiries'), {
    name: (name || '').trim() || '匿名ユーザー',
    email: (email || '').trim(),
    subject: 'ChemCraftお問い合わせ',
    message: (message || '').trim(),
    uid: user?.uid || null,
    source: 'chemcraft',
    status: 'new',
    createdAt: serverTimestamp(),
  })
}
